import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  ChevronLeft, ChevronRight, BookOpen, ShieldCheck, 
  Layers, Zap, Info, CheckCircle2, PlayCircle,
  X, HelpCircle, GraduationCap, ArrowRight,
  ClipboardList, AlertTriangle, Timer, Star,
  ShieldAlert, ClipboardCheck, Ban, MessageSquare, Ruler,
  Shield, UserCheck, AlertCircle, TrendingUp, Search,
  XCircle, Landmark, Keyboard, LayoutGrid, Maximize2, Minimize2,
  FileText, Users, UserSearch, CheckCircle, HeartPulse, Gauge,
  Accessibility, Clock, FileCode, Brain, Heart, Globe, Scale,
  Flame, Wind, Sparkles, Eye, Image as ImageIcon, Loader2,
  Stethoscope, Lightbulb,
  Printer,
  Volume2,
  VolumeX,
  Link as LinkIcon,
  Activity,
  ExternalLink,
  Video
} from 'lucide-react';
import { StudyModule, StudySlide, StudySlideType } from '../types';
import { IconResolver } from '../data/studyGuideData.js';
import { GamificationService } from '../services/gamificationService';
import { AdUnit } from '../components/AdUnit';
import { AdIntervalBreak } from '../components/AdIntervalBreak';
import { SponsoredService } from '../services/sponsoredService';
import { StorageService } from '../services/storageService';
import { PageLoader } from '../components/PageLoader';
import { useAuth } from '../components/AuthBridge';
import { SEO } from '../components/SEO';
import { ModuleHeader } from '../components/ModuleHeader';
import { AdvanceSuggestionPanel } from '../components/AdvanceSuggestionPanel';
import { GeminiService } from '../services/geminiService';
import { useThemeTokens } from '../hooks/useActiveTheme';
import { checkAndTriggerFirstQuizOfDayToast } from '../components/ToastNotification';
import { PrintStudyGuideModal, PrintScope } from '../components/PrintStudyGuideModal';
import { PrintStudyDocument } from '../components/PrintStudyDocument';
import { CNA_SKILLS } from '../data/skillsData';
import { downloadPDFFromElement } from '../services/pdfService';
import { MiniPlayer } from '../components/MiniPlayer';
import { getSafeEmbedUrl, getEmbedSandbox, EMBED_ALLOW_POLICY } from '../services/embedService';
import { EmbeddedPhasePlayer } from '../components/EmbeddedPhasePlayer';

// Dynamic category resolver according to study modules definition
const getStudyModuleCategory = (module: StudyModule) => {
  const title = module.title.toLowerCase();
  const id = module.id.toLowerCase();
  
  if (
    title.includes('intro') || title.includes('right') || title.includes('communication') || title.includes('cognitive') || title.includes('psychosocial') || title.includes('end-of-life') || title.includes('hospice') || title.includes('legal') ||
    id.includes('intro') || id.includes('right') || id.includes('communication') || id.includes('cognitive') || id.includes('end-of-life')
  ) {
    return {
      name: 'Principles & Rights',
      icon: Shield,
      color: 'indigo',
      badgeColor: 'bg-indigo-50 border-indigo-100 text-indigo-700 dark:bg-indigo-950/25 dark:border-indigo-900/50 dark:text-indigo-400',
      hoverColor: 'hover:border-indigo-300 dark:hover:border-indigo-800',
      textColor: 'text-indigo-600 dark:text-indigo-400'
    };
  }
  
  if (
    title.includes('infection') || title.includes('safety') || title.includes('emergency') || title.includes('mechanics') || title.includes('skin') || title.includes('pressure') || title.includes('hygiene') || title.includes('asepsis') ||
    id.includes('infection') || id.includes('emergency') || id.includes('mechanics') || id.includes('skin') || id.includes('safety')
  ) {
    return {
      name: 'Safety & Prevention',
      icon: ShieldAlert,
      color: 'rose',
      badgeColor: 'bg-rose-50 border-rose-100 text-rose-700 dark:bg-rose-950/25 dark:border-rose-900/50 dark:text-rose-400',
      hoverColor: 'hover:border-rose-300 dark:hover:border-rose-800',
      textColor: 'text-rose-600 dark:text-rose-450'
    };
  }
  
  if (
    title.includes('vital') || title.includes('weight') || title.includes('measure') || title.includes('conversion') || title.includes('nutrition') || title.includes('hydration') || title.includes('intake') || title.includes('output') ||
    id.includes('vital') || id.includes('weight') || id.includes('measure') || id.includes('nutrition')
  ) {
    return {
      name: 'Vitals & Metrics',
      icon: Activity,
      color: 'emerald',
      badgeColor: 'bg-emerald-50 border-emerald-100 text-emerald-700 dark:bg-emerald-950/25 dark:border-emerald-900/50 dark:text-emerald-400',
      hoverColor: 'hover:border-emerald-300 dark:hover:border-emerald-800',
      textColor: 'text-emerald-600 dark:text-emerald-400'
    };
  }
  
  if (
    title.includes('elimination') || title.includes('urinary') || title.includes('bowel') || title.includes('bath') || title.includes('personal') || title.includes('adl') || title.includes('groom') || title.includes('dress') || title.includes('hygiene') ||
    id.includes('elimination') || id.includes('personal') || id.includes('adl')
  ) {
    return {
      name: 'Personal ADL Care',
      icon: BookOpen,
      color: 'amber',
      badgeColor: 'bg-amber-50 border-amber-100 text-amber-700 dark:bg-amber-950/25 dark:border-amber-900/50 dark:text-amber-450',
      hoverColor: 'hover:border-amber-300 dark:hover:border-amber-800',
      textColor: 'text-amber-600 dark:text-amber-450'
    };
  }

  return {
    name: 'Advanced Care',
    icon: Layers,
    color: 'violet',
    badgeColor: 'bg-violet-50 border-violet-100 text-violet-700 dark:bg-violet-950/25 dark:border-violet-900/50 dark:text-violet-400',
    hoverColor: 'hover:border-violet-300 dark:hover:border-violet-800',
    textColor: 'text-violet-600 dark:text-violet-400'
  };
};

const getSlideTextContent = (slide: StudySlide): string => {
  if (!slide) return "";
  let content = `Slide Type: ${slide.type}\n`;
  if (slide.title) content += `Slide Title: ${slide.title}\n`;
  if (slide.data) {
    if (slide.data.importance) content += `Importance: ${slide.data.importance}\n`;
    if (slide.data.examContext) content += `Exam Context: ${slide.data.examContext}\n`;
    if (slide.data.situation) content += `Situation: ${slide.data.situation}\n`;
    if (slide.data.action) content += `Action: ${slide.data.action}\n`;
    if (slide.data.setupRationale) content += `Setup Rationale: ${slide.data.setupRationale}\n`;
    if (slide.data.instruction) content += `Instruction: ${slide.data.instruction}\n`;
    if (slide.data.sampleWording) content += `Sample Wording: ${slide.data.sampleWording}\n`;
    if (slide.data.bedsideMannerPrompt) content += `Bedside Manner: ${slide.data.bedsideMannerPrompt}\n`;
    if (slide.data.boardDifferentiators) content += `Board Differentiators: ${slide.data.boardDifferentiators}\n`;
    
    if (Array.isArray(slide.data.observations)) {
      content += `Observations:\n` + slide.data.observations.map((o: string) => `- ${o}`).join("\n") + "\n";
    }
    if (Array.isArray(slide.data.items)) {
      content += `Items:\n` + slide.data.items.map((i: any) => typeof i === 'string' ? `- ${i}` : `- ${i.term || i.name || i.title || ''}: ${i.definition || i.explanation || i.text || ''}`).join("\n") + "\n";
    }
    if (Array.isArray(slide.data.do)) {
      content += `Do (Authorized):\n` + slide.data.do.map((o: string) => `- ${o}`).join("\n") + "\n";
    }
    if (Array.isArray(slide.data.report)) {
      content += `Report:\n` + slide.data.report.map((o: string) => `- ${o}`).join("\n") + "\n";
    }
    if (Array.isArray(slide.data.dont)) {
      content += `Don't (Restricted):\n` + slide.data.dont.map((o: string) => `- ${o}`).join("\n") + "\n";
    }
    if (Array.isArray(slide.data.steps)) {
      content += `Steps / Process:\n` + slide.data.steps.map((o: string) => `- ${o}`).join("\n") + "\n";
    }
    if (Array.isArray(slide.data.facts)) {
      content += `High-Yield Facts:\n` + slide.data.facts.map((o: string) => `- ${o}`).join("\n") + "\n";
    }
    if (Array.isArray(slide.data.mistakes)) {
      content += `Common Mistakes:\n` + slide.data.mistakes.map((o: string) => `- ${o}`).join("\n") + "\n";
    }
    if (Array.isArray(slide.data.summary)) {
      content += `Summary Points:\n` + slide.data.summary.map((o: string) => `- ${o}`).join("\n") + "\n";
    }
    if (Array.isArray(slide.data.mnemonics)) {
      content += `Mnemonics:\n` + slide.data.mnemonics.map((o: string) => `- ${o}`).join("\n") + "\n";
    }
  }
  return content;
};

export const StudyGuide: React.FC = () => {
  const { certId } = useParams<{ certId: string }>(); 
  const { activeCert, user } = useAuth();
  const [config, setConfig] = useState(SponsoredService.getConfig());
  const tokens = useThemeTokens();

  useEffect(() => {
    const handleUpdate = () => {
      setConfig(SponsoredService.getConfig());
    };
    window.addEventListener('sponsored-config-updated', handleUpdate);
    return () => window.removeEventListener('sponsored-config-updated', handleUpdate);
  }, []);

  const [modules, setModules] = useState<StudyModule[]>([]);
  const [selectedModule, setSelectedModule] = useState<StudyModule | null>(null);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [completedModules, setCompletedModules] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategoryTab, setActiveCategoryTab] = useState<string>('all');
  const [expandedImage, setExpandedImage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetchingFullModule, setIsFetchingFullModule] = useState(false);
  const [showAdBreak, setShowAdBreak] = useState(false);
  const [lastAdIndex, setLastAdIndex] = useState(-1);

  const isNewUserFirstSession = useMemo(() => {
    if (!user) return false;
    // Recent registration (within 48 hours to handle mock accounts and first session window)
    const isRecent = user.createdAt ? (new Date().getTime() - new Date(user.createdAt).getTime() < 48 * 60 * 60 * 1000) : true;
    const sessionActive = sessionStorage.getItem('first_session_survey_eligible') !== 'false';
    const completedAlready = localStorage.getItem('completed_exit_intent_survey') === 'true';
    return isRecent && sessionActive && !completedAlready;
  }, [user]);

  // Quick Quiz State
  const [isQuizActive, setIsQuizActive] = useState(false);
  const [isGeneratingQuiz, setIsGeneratingQuiz] = useState(false);
  const [quizQuestions, setQuizQuestions] = useState<any[]>([]);
  const [currentQuizQuestionIndex, setCurrentQuizQuestionIndex] = useState(0);
  const [selectedQuizAnswers, setSelectedQuizAnswers] = useState<Record<string, string>>({});
  const [quizError, setQuizError] = useState<string | null>(null);

  // Print Modal State
  const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);
  const [printScope, setPrintScope] = useState<PrintScope>('study_guide');
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

  const handleConfirmPrint = (scope: PrintScope) => {
    setPrintScope(scope);
    setIsPrintModalOpen(false);
    setTimeout(() => {
      window.print();
      if (window.trackEvent) window.trackEvent('study_guide_printed', 'study', scope);
    }, 150);
  };

  const handleConfirmDownloadPdf = async (scope: PrintScope) => {
    setPrintScope(scope);
    setIsGeneratingPdf(true);
    setTimeout(async () => {
      try {
        const fileName = selectedModule && scope === 'current'
          ? `CNA_Study_Guide_${selectedModule.title.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`
          : scope === 'skills_lab'
          ? 'CNA_Clinical_Skills_Manual.pdf'
          : 'CNA_Complete_Study_Guide.pdf';

        await downloadPDFFromElement('print-study-document', { fileName });
        if (window.trackEvent) window.trackEvent('study_guide_pdf_downloaded', 'study', scope);
      } catch (err) {
        console.error('Failed to generate study guide PDF:', err);
      } finally {
        setIsGeneratingPdf(false);
        setIsPrintModalOpen(false);
      }
    }, 120);
  };
  
  const touchStart = useRef<{ x: number, y: number } | null>(null);

  const handleTriggerQuickQuiz = async () => {
    if (!selectedModule) return;
    const activeSlide = selectedModule.slides[currentSlideIndex];
    if (!activeSlide) return;

    setIsQuizActive(true);
    setIsGeneratingQuiz(true);
    setQuizError(null);
    setQuizQuestions([]);
    setCurrentQuizQuestionIndex(0);
    setSelectedQuizAnswers({});

    try {
      const slideText = getSlideTextContent(activeSlide);
      const questions = await GeminiService.generateQuickQuiz(
        slideText,
        selectedModule.title,
        activeCert?.name || 'Nursing Certification'
      );
      setQuizQuestions(questions);
    } catch (err: any) {
      console.error("Quiz generation failed:", err);
      setQuizError("Highly congested AI pathways. Please try again in a few seconds.");
    } finally {
      setIsGeneratingQuiz(false);
    }
  };

  const certPrefix = certId || 'cna';

  useEffect(() => {
    const loadData = async () => {
        setIsLoading(true);
        try {
            const data = await StorageService.getStudyModulesSummary(certPrefix);
            setModules(data);
        } catch (e) {
            console.error("Failed to load modules:", e);
        } finally {
            setIsLoading(false);
        }
    };
    loadData();
    const saved = localStorage.getItem(`${certPrefix}_completed_study_modules`);
    if (saved) setCompletedModules(JSON.parse(saved));
    else setCompletedModules([]);
  }, [certPrefix]);

  useEffect(() => {
    if (selectedModule) {
      window.dispatchEvent(
        new CustomEvent('update-breadcrumbs-nesting', {
          detail: {
            currentPageKey: 'guide',
            level2Label: selectedModule.title,
            onBackAction: 'guide-back-action',
          },
        })
      );
    } else {
      window.dispatchEvent(
        new CustomEvent('update-breadcrumbs-nesting', {
          detail: null,
        })
      );
    }
  }, [selectedModule]);

  useEffect(() => {
    const handleBack = () => {
      handleClose();
    };
    window.addEventListener('guide-back-action', handleBack);
    return () => {
      window.removeEventListener('guide-back-action', handleBack);
    };
  }, [selectedModule]);

  const handleOpenModule = async (summary: StudyModule) => {
    setIsFetchingFullModule(true);
    try {
        const fullModule = await StorageService.getStudyModuleById(certPrefix, summary.id);
        setSelectedModule(fullModule);
        setCurrentSlideIndex(0);
        setLastAdIndex(-1);
        setShowAdBreak(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        
        if (window.trackEvent) window.trackEvent('study_module_opened', 'study', fullModule.title);
    } catch (err) {
        console.error("Failed to hydrate study module:", err);
        alert("Registry sync error: Unable to load theoretical logic.");
    } finally {
        setIsFetchingFullModule(false);
    }
  };

  const [isSpeakingSlide, setIsSpeakingSlide] = useState(false);
  const activeSlideUtteranceRef = useRef<SpeechSynthesisUtterance | null>(null);

  // Stop speech when slide changes or module unmounts
  useEffect(() => {
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      activeSlideUtteranceRef.current = null;
    }
    setIsSpeakingSlide(false);
  }, [currentSlideIndex, selectedModule]);

  const handleToggleSlideAudio = () => {
    if (typeof window === 'undefined' || !('speechSynthesis' in window)) return;
    if (isSpeakingSlide) {
      window.speechSynthesis.cancel();
      activeSlideUtteranceRef.current = null;
      setIsSpeakingSlide(false);
      return;
    }

    if (!selectedModule || !selectedModule.slides[currentSlideIndex]) return;
    const currentSlide = selectedModule.slides[currentSlideIndex];

    const slideTitle = currentSlide.title || '';
    const contentText = currentSlide.content ? (Array.isArray(currentSlide.content) ? currentSlide.content.join('. ') : currentSlide.content) : '';
    const bulletsText = currentSlide.bullets ? currentSlide.bullets.join('. ') : '';
    const takeawayText = currentSlide.takeaway ? ` Key Takeaway: ${currentSlide.takeaway}` : '';
    const rawSpeech = `${slideTitle}. ${contentText} ${bulletsText} ${takeawayText}`;

    const cleanText = rawSpeech
      .replace(/<[^>]*>/g, ' ')
      .replace(/[*#`_~]/g, '')
      .replace(/\s+/g, ' ')
      .trim();

    if (!cleanText) return;

    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(cleanText);
    activeSlideUtteranceRef.current = utterance;
    utterance.onend = () => {
      activeSlideUtteranceRef.current = null;
      setIsSpeakingSlide(false);
    };
    utterance.onerror = () => {
      activeSlideUtteranceRef.current = null;
      setIsSpeakingSlide(false);
    };
    setIsSpeakingSlide(true);
    window.speechSynthesis.speak(utterance);
  };

  const handleClose = () => {
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      activeSlideUtteranceRef.current = null;
    }
    setIsSpeakingSlide(false);
    if (isNewUserFirstSession && selectedModule) {
      if ((window as any).triggerExitSurvey) {
        (window as any).triggerExitSurvey();
      }
    }
    setSelectedModule(null);
  };

  // SEO Schema for selected module
  const moduleSchema = useMemo(() => {
    if (!selectedModule) return undefined;
    return {
      "@context": "https://schema.org",
      "@type": "Course",
      "name": selectedModule.title,
      "description": `Comprehensive study unit for ${selectedModule.title} within the ${activeCert?.name || 'Medical'} certification curriculum.`,
      "provider": {
        "@type": "Organization",
        "name": "Diamond School Of Nursing",
        "sameAs": "https://www.diamondnursingschool.com"
      }
    };
  }, [selectedModule, activeCert]);

  const handleNextSlide = useCallback(() => {
    if (!selectedModule) return;
    
    const nextIndex = currentSlideIndex + 1;
    const interval = config.intervals.studyGuide || 5;

    // Trigger Ad Break logic - Only if intermissionEnabled is true
    if (user?.planTier === 'free' && config.isActive && config.intermissionEnabled && nextIndex > 0 && nextIndex % interval === 0 && lastAdIndex !== nextIndex) {
        setShowAdBreak(true);
        setLastAdIndex(nextIndex);
        return;
    }

    if (currentSlideIndex < selectedModule.slides.length - 1) {
      setCurrentSlideIndex(nextIndex);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else if (currentSlideIndex === selectedModule.slides.length - 1) {
      // Mark as complete and move to the reinforcement view
      if (!completedModules.includes(selectedModule.id)) {
        const newCompleted = [...completedModules, selectedModule.id];
        setCompletedModules(newCompleted);
        localStorage.setItem(`${certPrefix}_completed_study_modules`, JSON.stringify(newCompleted));
        GamificationService.addPoints(100);
      }
      setCurrentSlideIndex(nextIndex);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      handleClose();
    }
  }, [selectedModule, currentSlideIndex, completedModules, user?.planTier, config, lastAdIndex, certPrefix]);

  const handlePrevSlide = useCallback(() => {
    if (currentSlideIndex > 0) {
      setCurrentSlideIndex(prev => prev - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentSlideIndex]);

  const handlePrint = () => {
    setTimeout(() => {
        window.print();
        if (window.trackEvent && selectedModule) window.trackEvent('study_module_printed', 'study', selectedModule.title);
    }, 100);
  };

  const onTouchStart = (e: React.TouchEvent) => {
    touchStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
  };

  const onTouchEnd = (e: React.TouchEvent) => {
    if (!touchStart.current) return;
    const deltaX = e.changedTouches[0].clientX - touchStart.current.x;
    const deltaY = e.changedTouches[0].clientY - touchStart.current.y;
    touchStart.current = null;
    if (Math.abs(deltaX) > 50 && Math.abs(deltaX) > Math.abs(deltaY)) {
      if (deltaX > 0) handlePrevSlide();
      else handleNextSlide();
    }
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!selectedModule || showAdBreak) return;
      if (['INPUT', 'TEXTAREA'].includes((e.target as HTMLElement).tagName)) return;
      if (e.key === 'ArrowRight') handleNextSlide();
      else if (e.key === 'ArrowLeft') handlePrevSlide();
      else if (e.key === 'Escape') handleClose();
      else if (e.ctrlKey && e.key === 'p') {
        e.preventDefault();
        handlePrint();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedModule, handleNextSlide, handlePrevSlide, showAdBreak]);

  const filteredModules = useMemo(() => {
    return modules.filter(m => {
      const matchesSearch = m.title.toLowerCase().includes(searchTerm.toLowerCase());
      if (activeCategoryTab === 'all') return matchesSearch;
      const cat = getStudyModuleCategory(m).name;
      return matchesSearch && cat === activeCategoryTab;
    });
  }, [modules, searchTerm, activeCategoryTab]);

  const uniqueCategories = useMemo(() => {
    // Collect all category names in sorting order
    const categoriesSet = new Set(modules.map(m => getStudyModuleCategory(m).name));
    return Array.from(categoriesSet).sort();
  }, [modules]);

  if (isLoading) {
      return <PageLoader message="Hydrating Curriculum Registry..." text="Compiling study guide chapters..." />;
  }

  if (isFetchingFullModule) {
      return <PageLoader message="Loading Study Module..." text="Accessing Theoretical Registry Logic..." />;
  }

  if (showAdBreak) {
      return <AdIntervalBreak moduleName="Study Guide" onComplete={() => setShowAdBreak(false)} />;
  }

  return (
    <div className={selectedModule ? "w-full animate-fade-in text-slate-900 dark:text-slate-100 pb-20 no-print" : "max-w-7xl mx-auto pb-32 animate-fade-in text-slate-900 dark:text-slate-100 no-print"}>
      <SEO 
        title={selectedModule ? selectedModule.title : "Theory Lab"} 
        description={selectedModule ? `Official study unit for ${selectedModule.title}.` : undefined}
        schema={moduleSchema}
      />

      {!selectedModule ? (
        <div className="w-full pb-32 animate-fade-in text-slate-900 dark:text-slate-100">
          <AdUnit placement="study_guide" />
          
          {/* THEORETICAL CURRICULUM HEALTH DASHBOARD PROGRESS CARD */}
          <div className={`${tokens.heroBannerBg} rounded-[2.5rem] p-6 md:p-8 text-white shadow-2xl relative overflow-hidden border border-white/5 mb-8`}>
            <div className="absolute top-0 right-0 w-80 h-80 bg-white/5 blur-[80px] pointer-events-none -translate-y-1/2 translate-x-1/2"></div>
            <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
              
              <div className="lg:col-span-5 space-y-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="px-2.5 py-0.5 bg-white/10 text-white text-[8px] font-black rounded border border-white/20 uppercase tracking-widest flex items-center gap-1 leading-none pb-0.5">
                    <BookOpen size={10} className="animate-pulse" /> Curriculum Lab
                  </span>
                  <span className="px-2.5 py-0.5 bg-emerald-500/20 text-emerald-300 text-[8px] font-black rounded border border-emerald-500/30 uppercase tracking-widest font-extrabold leading-none pb-0.5">
                    BOARD REASONING
                  </span>
                </div>
                <h1 className="text-3xl md:text-4xl font-black tracking-tight text-white leading-none">Theoretical Study Guide</h1>
                <p className="text-xs text-slate-200 leading-relaxed font-semibold">
                  Lessons covering theoretical registry logic. Master the clinical reasoning required to bypass common traps on written state and national nursing boards.
                </p>
                <div className="pt-2 select-none">
                  <button 
                    onClick={() => {
                      setPrintScope(selectedModule ? 'current' : 'study_guide');
                      setIsPrintModalOpen(true);
                    }}
                    className="px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-[10px] font-black uppercase tracking-widest transition-all flex items-center gap-2 border border-indigo-400 shadow-lg active:scale-95 cursor-pointer"
                    id="print-study-guide-and-skills-btn"
                    title="Print formatted study guide and clinical skills manual PDF layout"
                  >
                    <Printer size={15} />
                    <span>Print Study Guide & Skill Lab</span>
                  </button>
                </div>
              </div>

              {/* Central Column Search Input */}
              <div className="lg:col-span-4 lg:px-4 py-1">
                <label className="text-[8px] font-black tracking-widest text-slate-200 uppercase mb-2 block font-extrabold">Theoretical Search Filter</label>
                <div className="relative group w-full">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:text-white transition-colors" size={16} />
                  <input 
                    type="text" 
                    placeholder="Search clinical topics..." 
                    value={searchTerm} 
                    onChange={e => setSearchTerm(e.target.value)} 
                    className="w-full pl-12 pr-10 py-3 bg-white/10 hover:bg-white/15 focus:bg-black/30 border border-white/20 rounded-2xl outline-none text-white text-xs placeholder:text-slate-300 placeholder:font-bold font-bold focus:border-white focus:ring-4 focus:ring-white/10 transition-all shadow-inner" 
                  />
                  {searchTerm && (
                    <button onClick={() => setSearchTerm('')} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-300 hover:text-white transition-colors">
                      <X size={14} />
                    </button>
                  )}
                </div>
              </div>

              {/* Central Column Progress Metric Card */}
              <div className="lg:col-span-3 bg-white/10 backdrop-blur-xl border border-white/20 p-5 rounded-[2rem] flex items-center justify-between shadow-xl">
                 <div className="space-y-1 text-left">
                     <p className="text-[8px] font-black uppercase text-slate-200 tracking-widest">Registry Mastery</p>
                     <p className="text-2xl font-black text-white">{completedModules.length} <span className="text-xs text-slate-300 font-bold">/ {modules.length}</span></p>
                     <div className="flex items-center gap-2">
                       <span className="text-[9px] font-black text-[#a7f3d0]">{modules.length > 0 ? Math.round((completedModules.length / modules.length) * 100) : 0}% Mastered</span>
                     </div>
                 </div>
                 <div className={`relative w-12 h-12 flex items-center justify-center ${tokens.iconBg} rounded-2xl border border-white/20 shrink-0`}>
                    <GraduationCap size={24} className="text-white" />
                 </div>
              </div>

            </div>
          </div>

          {/* HORIZONTAL CATEGORY NAVIGATION */}
          <div className="flex items-center gap-2 overflow-x-auto pb-4 mb-6 select-none no-scrollbar">
            <button 
              onClick={() => setActiveCategoryTab('all')}
              className={`px-5 py-2.5 rounded-full text-[10px] font-black tracking-widest transition-all shrink-0 uppercase border ${
                activeCategoryTab === 'all' 
                  ? tokens.activeTab
                  : tokens.inactiveTab
              }`}
            >
              All Lectures ({modules.length})
            </button>
            {uniqueCategories.map(cat => {
              const count = modules.filter(m => getStudyModuleCategory(m).name === cat).length;
              return (
                <button 
                  key={cat}
                  onClick={() => setActiveCategoryTab(cat)}
                  className={`px-5 py-2.5 rounded-full text-[10px] font-black tracking-widest transition-all border shrink-0 uppercase flex items-center gap-2 ${
                    activeCategoryTab === cat 
                      ? tokens.activeTab 
                      : tokens.inactiveTab
                  }`}
                >
                  <span>{cat}</span>
                  <span className="opacity-70 text-[8px]">({count})</span>
                </button>
              );
            })}
          </div>

          {/* Audio Summaries Mini Player Banner */}
          <div className="mb-8">
            <MiniPlayer variant="inline" />
          </div>

          {filteredModules.length === 0 ? (
            <div className="bg-white dark:bg-slate-900 rounded-[3rem] p-24 text-center border-4 border-dashed border-slate-100 dark:border-slate-800/80 flex flex-col items-center gap-6 grayscale opacity-60">
                <div className="w-20 h-20 bg-slate-50 dark:bg-slate-850 rounded-full flex items-center justify-center shadow-inner">
                    <BookOpen size={36} className="text-slate-300 dark:text-slate-600" />
                </div>
                <div className="space-y-1">
                  <p className="text-xl font-black text-slate-900 dark:text-white tracking-tighter">No Lectures Found</p>
                  <p className="text-xs font-bold text-slate-400 tracking-widest">Modify your query or select "All Lectures" above.</p>
                </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {filteredModules.map((module) => {
                const Icon = IconResolver(module.icon);
                const isDone = completedModules.includes(module.id);
                const catInfo = getStudyModuleCategory(module);
                const CatIcon = catInfo.icon;
                const hasVideo = (module.slides || []).some(s => s.type === 'embedded' || s.data?.embedUrl || s.data?.embedCode || s.data?.videoUrl || s.data?.youtubeUrl) || !!module.videoUrl || !!(module as any).hasVideo;
                
                return (
                  <button 
                    key={module.id} 
                    onClick={() => handleOpenModule(module)} 
                    className={`group relative bg-white dark:bg-slate-900 rounded-[2.5rem] border border-slate-200 dark:border-slate-800 text-left transition-all hover:shadow-2xl overflow-hidden flex flex-col shadow-sm duration-350 active:scale-[0.98] ${tokens.accentBorder}`}
                  >
                    <div className="aspect-[16/10] w-full relative overflow-hidden shrink-0 bg-slate-905">
                      <div className="absolute inset-0 bg-gradient-to-br from-slate-800 to-slate-950 opacity-90 transition-opacity group-hover:opacity-95"></div>
                      <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '16px 16px' }}></div>
                      
                      {isDone && (
                        <div className="absolute top-5 right-5 px-3 py-1.5 bg-emerald-500 text-white rounded-full text-[9px] font-black shadow-lg flex items-center gap-1.5 z-20 border border-emerald-400">
                          <CheckCircle size={11} fill="white" className="text-emerald-505" /> Mastery Achieved
                        </div>
                      )}

                      <div className="absolute top-5 left-5 flex items-center gap-1.5 z-20">
                        <div className="px-3 py-1 bg-white/10 backdrop-blur-md text-white rounded-full text-[8px] font-black tracking-widest uppercase border border-white/5 flex items-center gap-1 leading-none">
                          <CatIcon size={10} /> {catInfo.name}
                        </div>
                        {hasVideo && (
                          <div className="px-2.5 py-1 bg-indigo-600/90 backdrop-blur-md text-white rounded-full text-[7.5px] font-black tracking-wider uppercase border border-indigo-400/40 flex items-center gap-1 shadow-sm leading-none" title="Video Lesson Included">
                            <PlayCircle size={9} className="text-white shrink-0" /> Video
                          </div>
                        )}
                      </div>
                      
                      <div className="relative z-10 w-full h-full flex flex-col items-center justify-center gap-3 transition-transform duration-700 group-hover:scale-105">
                        <div className={`p-4 ${tokens.iconBg} backdrop-blur-xl rounded-2xl border border-white/25 shadow-2x`}>
                           <Icon size={32} className="text-white drop-shadow-[0_0_15px_rgba(255,255,255,0.3)]" strokeWidth={1.5} />
                        </div>
                        <div className="text-center px-6">
                          <p className={`text-[9px] font-black ${tokens.accentText} tracking-[0.3em] leading-none mb-1 opacity-80`}>THEORETICAL UNIT</p>
                          <h3 className="text-base font-black text-white tracking-tight leading-tight line-clamp-2 flex items-center justify-center gap-1.5">
                            <span>{module.title.replace(/^\d+\.\s*/, '')}</span>
                            {hasVideo && (
                              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-indigo-600/90 text-white rounded-md text-[8px] font-black tracking-normal border border-indigo-400/50 shadow-xs shrink-0" title="Video Lesson Included">
                                <PlayCircle size={10} className="shrink-0" />
                                <span>Video</span>
                              </span>
                            )}
                          </h3>
                        </div>
                      </div>

                      <div className="absolute bottom-0 left-0 right-0 h-1.5 bg-slate-800">
                        <div className={`h-full ${tokens.progressBar} transition-all duration-1000 ${isDone ? 'w-full' : 'w-0 group-hover:w-1/3'}`}></div>
                      </div>
                    </div>
                    
                    <div className="p-6 md:p-8 space-y-4 flex-1 flex flex-col justify-between">
                      <p className="text-xs text-slate-500 dark:text-slate-400 font-medium leading-relaxed line-clamp-3">
                           Comprehensive clinical logic and theoretical foundation for {module.title.replace(/^\d+\.\s*/, '')}. Optimized for written state registry testing standards.
                      </p>
                      <div className="flex items-center justify-between pt-4 border-t border-slate-100 dark:border-slate-800/60">
                        <div className="flex items-center gap-3">
                          <span className="text-[10px] font-black text-slate-400 tracking-widest flex items-center gap-2 uppercase">
                             <Clock size={12} /> 15 min read
                          </span>
                          {hasVideo && (
                            <span className="text-[9px] font-black text-indigo-600 dark:text-indigo-400 tracking-wider flex items-center gap-1 uppercase bg-indigo-50 dark:bg-indigo-950/60 px-2 py-0.5 rounded-md border border-indigo-200/80 dark:border-indigo-900/60" title="Video Lesson Included">
                              <PlayCircle size={10} className="shrink-0" /> Video
                            </span>
                          )}
                        </div>
                        <div className={`w-10 h-10 bg-slate-50 dark:bg-slate-800 group-hover:${tokens.primaryBtn} rounded-full flex items-center justify-center text-slate-400 dark:text-slate-500 group-hover:text-white dark:group-hover:text-white transition-all shadow-inner`}>
                           <ArrowRight size={18} className="group-hover:translate-x-0.5 transition-transform" />
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      ) : (
        <div className="animate-pop w-full flex flex-col" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
          {/* LMS Header */}
          <header className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 px-4 md:px-6 py-3.5 flex items-center justify-between shadow-sm no-print rounded-3xl mb-6">
            <div className="flex items-center gap-4 min-w-0">
              <button 
                onClick={handleClose} 
                className="flex items-center gap-1 px-4 py-2 bg-gradient-to-r from-rose-500 to-rose-600 hover:from-rose-600 hover:to-rose-700 text-white font-extrabold text-[10px] tracking-widest uppercase rounded-xl transition-all shadow-[0_4px_12px_rgba(244,63,94,0.3)] active:scale-95 group shrink-0 border border-rose-400/20"
                aria-label="Exit current lesson and return to modules catalog"
              >
                <ChevronLeft size={16} strokeWidth={3} className="group-hover:-translate-x-0.5 transition-transform" />
                <span className="hidden sm:inline">Close Lesson</span>
              </button>
              <div className="min-w-0 pr-2">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-[8px] md:text-[9px] font-black text-indigo-500 tracking-widest bg-indigo-50 dark:bg-indigo-950/40 px-2 py-0.5 rounded-full border border-indigo-100 dark:border-indigo-900">Study Unit</span>
                  {completedModules.includes(selectedModule.id) && <CheckCircle size={10} className="text-emerald-500" />}
                </div>
                <h1 className="text-xs md:text-base font-black text-slate-900 dark:text-white truncate leading-tight flex items-center gap-2">
                  <span className="truncate">{selectedModule.title}</span>
                  {(selectedModule.slides || []).some(s => s.type === 'embedded' || s.data?.embedUrl || s.data?.embedCode || s.data?.videoUrl || s.data?.youtubeUrl) && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 rounded-md text-[8px] font-black border border-indigo-200 dark:border-indigo-800 shrink-0" title="Video Lesson Included">
                      <PlayCircle size={9} className="shrink-0" />
                      <span>Video</span>
                    </span>
                  )}
                </h1>
              </div>
            </div>

            <div className="flex items-center gap-4 shrink-0">
              <div className="hidden lg:flex flex-col items-end mr-4">
                <div className="flex items-center gap-3 mb-1">
                  <span className="text-[9px] font-black text-slate-400 tracking-widest font-black uppercase">Mastery</span>
                  <span className="text-[11px] font-black text-indigo-600 dark:text-indigo-400">
                    {Math.round(((currentSlideIndex + 1) / (selectedModule.slides.length + 1)) * 100)}%
                  </span>
                </div>
                <div className="w-32 h-1 bg-slate-150 dark:bg-slate-950 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-indigo-500 transition-all duration-700 ease-out" 
                    style={{ width: `${((currentSlideIndex + 1) / (selectedModule.slides.length + 1)) * 100}%` }}
                  ></div>
                </div>
              </div>

              <div className="flex gap-2 flex-wrap justify-end">
                {currentSlideIndex < selectedModule.slides.length && (
                  <button 
                    onClick={handleToggleSlideAudio}
                    className={`px-3 py-2.5 rounded-xl transition-all active:scale-95 flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest cursor-pointer shadow-sm border ${
                      isSpeakingSlide 
                        ? 'bg-rose-50 text-rose-600 border-rose-200 dark:bg-rose-950/30 dark:border-rose-800 animate-pulse' 
                        : 'bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 border-slate-200 dark:border-slate-700'
                    }`}
                    title={isSpeakingSlide ? "Stop reading lesson" : "Listen to lesson slide (Text-to-Speech)"}
                    aria-label={isSpeakingSlide ? "Stop reading lesson" : "Listen to lesson slide aloud"}
                  >
                    {isSpeakingSlide ? <VolumeX size={15} className="text-rose-500" /> : <Volume2 size={15} />}
                    <span className="hidden sm:inline">{isSpeakingSlide ? 'Stop' : 'Listen'}</span>
                  </button>
                )}
                {currentSlideIndex < selectedModule.slides.length && config.isQuickQuizEnabled !== false && (
                  <button 
                    onClick={handleTriggerQuickQuiz}
                    className="px-3.5 py-2.5 bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 text-white rounded-xl transition-all active:scale-95 flex items-center gap-2 text-[10px] font-black uppercase tracking-widest cursor-pointer shadow-md hover:shadow-lg border border-indigo-500/20"
                    title="Take an 8-question quick quiz based specifically on this page!"
                    id="trigger-quick-quiz-btn"
                  >
                    <Brain size={15} className="animate-pulse shrink-0" />
                    <span>Quick Quiz</span>
                  </button>
                )}
                <button 
                  onClick={() => {
                    setPrintScope('current');
                    setIsPrintModalOpen(true);
                  }}
                  className="px-3.5 py-2.5 bg-indigo-50 hover:bg-indigo-100 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300 rounded-xl border border-indigo-200 dark:border-indigo-800 transition-all active:scale-95 flex items-center gap-2 text-[10px] font-black uppercase tracking-widest cursor-pointer shrink-0 font-bold"
                  title="Print current unit or full study guide and skills manual"
                  id="print-study-guide-btn"
                >
                  <Printer size={15} />
                  <span>Print Study Guide</span>
                </button>
                <button 
                  onClick={handleNextSlide} 
                  className="px-4 md:px-8 py-2 md:py-2.5 rounded-xl font-black text-xs tracking-widest shadow-lg transition-all active:scale-95 bg-slate-900 dark:bg-white text-white dark:text-slate-900 border-b-2 border-black dark:border-slate-200 flex items-center gap-2 uppercase"
                >
                  {currentSlideIndex === selectedModule.slides.length ? 'Exit' : (currentSlideIndex === selectedModule.slides.length - 1 ? 'Complete' : 'Continue')}
                  <ArrowRight size={14} />
                </button>
              </div>
            </div>
          </header>

          <div className="flex flex-col lg:flex-row gap-8 items-start w-full relative no-print">
            {/* Curriculum Sidebar */}
            <aside className="hidden lg:flex w-72 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-[2rem] p-5 shadow-xs flex-col lg:sticky lg:top-24 no-print shrink-0">
              <div className="p-4 border-b border-slate-100 dark:border-slate-800 bg-slate-50/30 dark:bg-slate-950/50 flex items-center justify-between rounded-t-2xl">
                <h3 className="text-[9px] font-black text-slate-400 dark:text-slate-500 tracking-[0.2em] uppercase">Curriculum Timeline</h3>
                <Layers size={14} className="text-slate-300 dark:text-slate-650" />
              </div>
              <div className="p-2 space-y-1 max-h-[calc(100vh-280px)] overflow-y-auto custom-scrollbar">
                {selectedModule.slides.map((slide, i) => {
                  const isCompleted = i < currentSlideIndex;
                  const isActive = i === currentSlideIndex;
                  const slideHasVideo = slide.type === 'embedded' || Boolean(slide.data?.embedUrl) || Boolean(slide.data?.embedCode) || Boolean(slide.data?.videoUrl) || Boolean(slide.data?.youtubeUrl) || Boolean((slide as any).videoUrl);

                  return (
                    <button 
                      key={slide.id}
                      onClick={() => setCurrentSlideIndex(i)}
                      className={`w-full group p-3 rounded-xl flex items-center gap-3 transition-all text-left ${isActive ? 'bg-indigo-50 dark:bg-indigo-950/20 border border-indigo-100 dark:border-indigo-900 shadow-sm' : 'hover:bg-slate-50 dark:hover:bg-slate-950'}`}
                    >
                      <div className={`h-6 w-6 rounded-lg border-2 flex items-center justify-center shrink-0 transition-all ${
                        isCompleted ? 'bg-emerald-500 border-emerald-500 text-white' : 
                        isActive ? 'bg-indigo-600 border-indigo-600 text-white' : 'border-slate-200 dark:border-slate-800 text-slate-400 dark:text-slate-550 bg-white dark:bg-slate-950'
                      }`}>
                        {isCompleted ? <CheckCircle size={12} strokeWidth={3} /> : <span className="text-[10px] font-black">{i + 1}</span>}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5 truncate">
                          {slideHasVideo && <PlayCircle size={11} className="text-indigo-600 dark:text-indigo-400 shrink-0" />}
                          <p className={`text-[10px] font-black tracking-tight truncate ${isActive ? 'text-indigo-900 dark:text-indigo-200' : 'text-slate-600 dark:text-slate-400'}`}>{slide.title || slide.type}</p>
                        </div>
                        <p className="text-[8px] font-bold text-slate-400 dark:text-slate-500 tracking-widest leading-none mt-0.5 uppercase flex items-center gap-1">
                          <span>{slide.type === 'embedded' ? 'Video Demonstration' : slide.type}</span>
                          {slideHasVideo && slide.type !== 'embedded' && (
                            <span className="text-indigo-600 dark:text-indigo-400 font-extrabold flex items-center gap-0.5">
                              • <Video size={8} /> Video
                            </span>
                          )}
                        </p>
                      </div>
                    </button>
                  );
                })}
                <div className="my-3 border-t border-slate-100 dark:border-slate-800 py-2">
                    <button 
                    onClick={() => setCurrentSlideIndex(selectedModule.slides.length)}
                    className={`w-full group p-3 rounded-xl flex items-center gap-3 transition-all text-left ${currentSlideIndex === selectedModule.slides.length ? 'bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-100 dark:border-emerald-900 shadow-sm' : 'hover:bg-slate-50 dark:hover:bg-slate-955'}`}
                    >
                    <div className={`h-6 w-6 rounded-lg border-2 flex items-center justify-center shrink-0 transition-all ${
                        currentSlideIndex === selectedModule.slides.length ? 'bg-emerald-600 border-emerald-600 text-white' : 'border-slate-200 dark:border-slate-800 text-slate-400 dark:text-slate-550 bg-white dark:bg-slate-950'
                    }`}>
                        <Star size={12} strokeWidth={3} />
                    </div>
                    <div className="min-w-0">
                        <p className={`text-[10px] font-black tracking-tight truncate ${currentSlideIndex === selectedModule.slides.length ? 'text-emerald-900 dark:text-emerald-200' : 'text-slate-600 dark:text-slate-400'}`}>Lesson Synthesis</p>
                        <p className="text-[8px] font-bold text-slate-400 dark:text-slate-500 tracking-widest leading-none mt-0.5 uppercase">Final Review</p>
                    </div>
                    </button>
                </div>
              </div>
              <div className="p-4 mt-auto">
                <div className="p-4 bg-slate-900 dark:bg-slate-950 rounded-2xl text-white space-y-2 shadow-xl text-left border dark:border-slate-800">
                    <div className="flex items-center gap-2">
                        <Gauge size={14} className="text-medical-400" />
                        <span className="text-[8px] font-black tracking-widest">Focus Level</span>
                    </div>
                    <p className="text-[9px] text-slate-300 dark:text-slate-400 font-medium leading-relaxed">"Read carefully. Board questions often pivot on small semantic details."</p>
                </div>
              </div>
            </aside>

            {/* Learning Stage */}
            <main className="flex-1 w-full min-w-0 no-print">
              <div className="max-w-4xl mx-auto w-full">
                <div key={`${selectedModule.id}-${currentSlideIndex}`} className="w-full animate-slide-up pb-24">
                  {currentSlideIndex < selectedModule.slides.length ? (
                    <div className="space-y-6">
                       {/* Study Guide Ads Top */}
                       <AdUnit placement="study_guide_top" className="mb-6" />

                       {/* Slide Navigation Context */}
                       <div className="flex items-center justify-between px-1 mb-2">
                         <div className="flex items-center gap-3">
                           <div className="w-8 h-8 rounded-full bg-indigo-600 flex items-center justify-center text-white font-black text-xs shadow-lg">{currentSlideIndex + 1}</div>
                           <span className="text-[10px] font-black text-slate-900 tracking-widest">Phase {currentSlideIndex + 1} of {selectedModule.slides.length}</span>
                         </div>
                         <div className="lg:hidden text-[9px] font-black text-slate-400 tracking-widest">
                            Slide {currentSlideIndex + 1} / {selectedModule.slides.length}
                         </div>
                       </div>

                       {renderModuleSlide(selectedModule, selectedModule.slides[currentSlideIndex], currentSlideIndex, setExpandedImage)}
                       
                       <div className="flex items-center justify-between mt-10 pt-8 border-t border-slate-200">
                         <button 
                           onClick={handlePrevSlide} 
                           disabled={currentSlideIndex === 0}
                           className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-black text-[10px] tracking-widest transition-all ${
                             currentSlideIndex === 0 ? 'opacity-0 pointer-events-none' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
                           }`}
                         >
                           <ChevronLeft size={16} /> Back
                         </button>
                         <button 
                           onClick={handleNextSlide} 
                           className="flex items-center gap-3 px-8 py-3 bg-slate-900 text-white rounded-xl font-black text-[10px] tracking-widest shadow-xl transition-all active:scale-95 group translate-y-[-2px] border-b-2 border-black"
                         >
                           {currentSlideIndex === selectedModule.slides.length - 1 ? 'Finish Lesson' : 'Next Step'} <ChevronRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
                         </button>
                       </div>

                       {/* Study Guide Ads Bottom */}
                       <AdUnit placement="study_guide_bottom" className="mt-12" />
                    </div>
                  ) : (
                    <div className="mt-4 md:mt-8 bg-gradient-to-br from-white to-slate-50 rounded-[2.5rem] p-8 md:p-14 border border-indigo-100 shadow-2xl animate-pop relative overflow-hidden">
                      <div className="absolute top-0 right-0 w-80 h-80 bg-indigo-500/5 blur-[100px] -translate-y-1/2 translate-x-1/2"></div>
                      
                      <div className="max-w-3xl mx-auto text-center space-y-10 relative z-10">
                        <div className="space-y-4">
                          <div className="w-24 h-24 bg-emerald-50 text-emerald-600 rounded-[2.5rem] flex items-center justify-center mx-auto shadow-sm border border-emerald-100">
                            <CheckCircle2 size={48} />
                          </div>
                          <h2 className="text-3xl md:text-5xl font-black text-slate-900 tracking-tight">Lesson Mastered</h2>
                          <div className="flex items-center justify-center gap-2">
                             <div className="h-1 w-12 bg-emerald-500 rounded-full"></div>
                             <span className="text-[10px] font-black text-slate-400 tracking-[0.3em]">Knowledge Registry Updated</span>
                             <div className="h-1 w-12 bg-emerald-500 rounded-full"></div>
                          </div>
                        </div>

                        <div className="bg-slate-900 rounded-[2.5rem] p-8 md:p-12 text-white space-y-6 shadow-2xl relative overflow-hidden text-left">
                          <div className="absolute top-0 right-0 p-10 opacity-5"><Brain size={120}/></div>
                          <div className="inline-flex items-center gap-3 px-4 py-2 bg-indigo-500/20 text-indigo-300 rounded-full text-[10px] font-black tracking-widest border border-indigo-500/30">
                            <Zap size={14} className="animate-pulse" /> Active Reinforcement
                          </div>
                          <p className="text-lg md:text-2xl font-medium text-slate-300 leading-relaxed relative z-10">
                            "You have successfully synthesized the theoretical logic for this unit. Now, move to the Practice Lab or Skills Lab to apply this knowledge in board-style simulations."
                          </p>
                        </div>

                        <div className="pt-4 text-left">
                          <AdvanceSuggestionPanel 
                            source="guide"
                            moduleId={selectedModule.id}
                            moduleTitle={selectedModule.title} 
                          />
                        </div>

                        <div className="pt-8 border-t border-slate-100">
                           <button 
                             onClick={handleClose}
                             className="px-8 py-4 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-2xl text-[10px] font-black tracking-[0.3em] transition-all active:scale-95 flex items-center gap-3 mx-auto"
                           >
                             <ArrowRight size={16} className="rotate-180" /> Return to Catalog
                           </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </main>
          </div>

          {/* Print-Only Formatted Layout */}
          <PrintStudyDocument
            modules={modules}
            skills={CNA_SKILLS}
            selectedModule={selectedModule}
            scope={printScope}
            certName={activeCert?.name || "Nurse Aide Certification (CNA)"}
            renderModuleSlide={renderModuleSlide}
          />

          {/* Export & Print Options Modal */}
          <PrintStudyGuideModal
            isOpen={isPrintModalOpen}
            onClose={() => setIsPrintModalOpen(false)}
            onConfirmPrint={handleConfirmPrint}
            onConfirmDownloadPdf={handleConfirmDownloadPdf}
            isGeneratingPdf={isGeneratingPdf}
            currentTitle={selectedModule?.title}
            isCurrentLessonActive={!!selectedModule}
            totalModulesCount={modules.length}
            totalSkillsCount={CNA_SKILLS.length}
          />

          {/* Quick Quiz Overlay Modal */}
          {isQuizActive && (
            <div className="fixed inset-0 z-50 overflow-y-auto no-print flex items-center justify-center p-4">
              {/* Backdrop */}
              <div 
                className="fixed inset-0 bg-slate-900/60 backdrop-blur-md transition-opacity" 
                onClick={() => {
                  if (Object.keys(selectedQuizAnswers).length === quizQuestions.length && quizQuestions.length > 0) {
                    setIsQuizActive(false);
                  } else if (window.confirm("Are you sure you want to exit the Quick Quiz? Your progress will be lost.")) {
                    setIsQuizActive(false);
                  }
                }}
              />

              {/* Modal Card Centering Container */}
              <div className="relative transform overflow-hidden rounded-[2.5rem] bg-white dark:bg-slate-900 p-6 md:p-10 text-left shadow-2xl transition-all w-full max-w-2xl border border-slate-200 dark:border-slate-800 animate-slide-up z-10">
                
                {/* CLOSE BUTTON */}
                <button 
                  onClick={() => {
                    if (Object.keys(selectedQuizAnswers).length === quizQuestions.length && quizQuestions.length > 0) {
                      setIsQuizActive(false);
                    } else if (window.confirm("Are you sure you want to exit the Quick Quiz? Your progress will be lost.")) {
                      setIsQuizActive(false);
                    }
                  }}
                  className="absolute right-6 top-6 p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 transition-all cursor-pointer animate-fade-in"
                >
                  <X size={20} strokeWidth={2.5} />
                </button>

                {/* LOADING STATE */}
                {isGeneratingQuiz && (
                  <PageLoader compact message="Synthesizing Clinical Quiz" text="AI compiling targeted protocol questions..." />
                )}

                {/* ERROR STATE */}
                {quizError && !isGeneratingQuiz && (
                  <div className="py-16 flex flex-col items-center justify-center space-y-6 text-center animate-fade-in">
                    <div className="w-16 h-16 bg-rose-50 dark:bg-rose-950/20 text-rose-500 rounded-2xl flex items-center justify-center shadow-lg border border-rose-100 dark:border-rose-900/30">
                      <AlertTriangle size={32} />
                    </div>
                    <div className="space-y-2 max-w-md">
                      <h3 className="text-lg font-black text-slate-900 dark:text-white tracking-tight">Quiz Synthesis Stalled</h3>
                      <p className="text-xs font-bold text-rose-600 dark:text-rose-400 leading-relaxed">{quizError}</p>
                    </div>
                    <button 
                      onClick={handleTriggerQuickQuiz}
                      className="px-6 py-3 bg-gradient-to-r from-indigo-600 to-indigo-700 text-white rounded-xl shadow-lg hover:shadow-xl hover:from-indigo-700 hover:to-indigo-800 transition-all text-xs font-black uppercase tracking-widest active:scale-95 cursor-pointer"
                    >
                      Retry Generation
                    </button>
                  </div>
                )}

                {/* ACTIVE QUIZ GAMEPLAY */}
                {!isGeneratingQuiz && !quizError && quizQuestions.length > 0 && (
                  <div className="animate-fade-in">
                    {/* HEADER SUMMARY PRECIS */}
                    <div className="border-b border-slate-100 dark:border-slate-850 pb-5 mb-6 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2.5 bg-indigo-50 dark:bg-indigo-950/30 text-indigo-600 dark:text-indigo-400 rounded-xl border border-indigo-100 dark:border-indigo-900 shadow-sm shrink-0">
                          <Brain size={20} />
                        </div>
                        <div>
                          <span className="text-[9px] font-black tracking-widest text-indigo-500 uppercase">Active Slide Quiz</span>
                          <h3 className="text-base font-black text-slate-900 dark:text-white leading-none tracking-tight mt-1">{selectedModule.title}</h3>
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <span className="text-[10px] font-black text-slate-400 dark:text-slate-500 tracking-widest uppercase">Question</span>
                        <p className="text-sm font-black text-indigo-600 dark:text-indigo-400">{currentQuizQuestionIndex + 1} <span className="text-slate-350 dark:text-slate-650">/ 8</span></p>
                      </div>
                    </div>

                    {/* QUESTION CARD */}
                    {currentQuizQuestionIndex < quizQuestions.length ? (
                      (() => {
                        const question = quizQuestions[currentQuizQuestionIndex];
                        const selectedOptionId = selectedQuizAnswers[question._id];
                        const hasAnswered = !!selectedOptionId;

                        return (
                          <div className="space-y-6">
                            {/* Slide Progress Dots */}
                            <div className="flex gap-1.5 justify-center mb-4">
                              {quizQuestions.map((_, i) => {
                                const ansId = quizQuestions[i]._id;
                                const answered = !!selectedQuizAnswers[ansId];
                                const isCurrent = i === currentQuizQuestionIndex;
                                let dotClass = "h-2 w-2 rounded-full transition-all ";
                                if (isCurrent) dotClass += "bg-indigo-600 dark:bg-indigo-400 w-5";
                                else if (answered) dotClass += "bg-emerald-500";
                                else dotClass += "bg-slate-200 dark:bg-slate-800";
                                return <div key={i} className={dotClass} />;
                              })}
                            </div>

                            {/* Question Narrative */}
                            <div className="bg-slate-50 dark:bg-slate-950/40 p-6 rounded-[2rem] border border-slate-150/50 dark:border-slate-850/50 animate-fade-in">
                              <span className="text-[10px] font-black text-indigo-600 dark:text-indigo-400 tracking-widest uppercase mb-2 block">Clinical Inquiry</span>
                              <h4 className="text-sm font-black text-slate-905 dark:text-white leading-relaxed">{question.text}</h4>
                            </div>

                            {/* Multiple Choice Options Grid */}
                            <div className="grid grid-cols-1 gap-3.5 pt-2">
                              {question.options.map((option: any) => {
                                const isSelected = selectedOptionId === option._id;
                                let btnStyle = "w-full text-left p-4 rounded-2xl border-2 transition-all duration-200 flex items-center justify-between text-xs font-black leading-relaxed ";
                                
                                if (!hasAnswered) {
                                  btnStyle += "border-slate-200 dark:border-slate-850 bg-white hover:bg-slate-50 dark:bg-slate-950 dark:hover:bg-slate-850 hover:border-indigo-400 dark:hover:border-indigo-900 text-slate-800 dark:text-slate-200 cursor-pointer active:scale-[0.99]";
                                } else {
                                  if (option.isCorrect) {
                                    btnStyle += "border-emerald-500 dark:border-emerald-650 bg-emerald-50/50 dark:bg-emerald-950/20 text-emerald-900 dark:text-emerald-300";
                                  } else if (isSelected) {
                                    btnStyle += "border-rose-500 dark:border-rose-700 bg-rose-5/50 dark:bg-rose-950/20 text-rose-900 dark:text-rose-300";
                                  } else {
                                    btnStyle += "border-slate-100 dark:border-slate-850 bg-white dark:bg-slate-950 text-slate-400 dark:text-slate-600 opacity-60 cursor-not-allowed";
                                  }
                                }

                                return (
                                  <button 
                                    key={option._id}
                                    onClick={() => {
                                      if (!hasAnswered) {
                                        setSelectedQuizAnswers(prev => ({
                                          ...prev,
                                          [question._id]: option._id
                                        }));
                                      }
                                    }}
                                    disabled={hasAnswered}
                                    className={btnStyle}
                                  >
                                    <span className="pr-4">{option.text}</span>
                                    {hasAnswered && (
                                      option.isCorrect ? (
                                        <CheckCircle className="text-emerald-500 shrink-0" size={18} />
                                      ) : isSelected ? (
                                        <XCircle className="text-rose-500 shrink-0" size={18} />
                                      ) : null
                                    )}
                                  </button>
                                );
                              })}
                            </div>

                            {/* EXPLANATION AND NEXT CONTROLS (Slide in once answered) */}
                            {hasAnswered && (
                              <div className="mt-6 pt-6 border-t border-slate-100 dark:border-slate-850 space-y-5 animate-slide-up">
                                <div className="bg-emerald-50/40 dark:bg-emerald-950/10 p-5 rounded-2xl border border-emerald-100/50 dark:border-emerald-900/30">
                                  <h5 className="text-[10px] font-black text-emerald-600 dark:text-emerald-400 tracking-widest uppercase mb-1.5 flex items-center gap-1.5 animate-pulse">
                                    <Sparkles size={12} className="text-emerald-500" /> Clinical Mastery Explanation
                                  </h5>
                                  <p className="text-[11px] font-bold text-slate-700 dark:text-slate-350 leading-relaxed">{question.explanation}</p>
                                </div>

                                <div className="flex justify-end pt-2">
                                  <button 
                                    onClick={() => {
                                      setCurrentQuizQuestionIndex(prev => prev + 1);
                                    }}
                                    className="px-8 py-3.5 bg-slate-950 dark:bg-white text-white dark:text-slate-950 rounded-xl text-xs font-black uppercase tracking-widest hover:bg-slate-800 dark:hover:bg-slate-100 transition-all active:scale-95 flex items-center gap-2 cursor-pointer shadow-md"
                                  >
                                    <span>{currentQuizQuestionIndex < quizQuestions.length - 1 ? "Next Question" : "View Scorecard"}</span>
                                    <ChevronRight size={15} strokeWidth={2.5} />
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })()
                    ) : (
                      /* SCORECARD COMPLETION REPORT */
                      (() => {
                        const totalQuestions = quizQuestions.length;
                        let correctCount = 0;
                        quizQuestions.forEach(q => {
                          const selectedOptId = selectedQuizAnswers[q._id];
                          const correctOpt = q.options.find((o: any) => o.isCorrect);
                          if (correctOpt && correctOpt._id === selectedOptId) {
                            correctCount++;
                          }
                        });
                        const scorePercent = Math.round((correctCount / totalQuestions) * 100);
                        const isPassed = scorePercent >= 75;

                        // Trigger first quiz of the day toast notification
                        checkAndTriggerFirstQuizOfDayToast();

                        return (
                          <div className="space-y-8 py-4 animate-fade-in text-center">
                            <div className="flex flex-col items-center justify-center space-y-4">
                              {/* Large Score Ring */}
                              <div className="relative flex items-center justify-center h-28 w-28 rounded-full border-4 border-slate-100 dark:border-slate-800 shadow-sm bg-slate-50/50 dark:bg-slate-950">
                                <div className="text-center">
                                  <p className="text-3xl font-black text-slate-900 dark:text-white leading-none">{scorePercent}%</p>
                                  <p className="text-[9px] font-black text-slate-400 dark:text-slate-500 tracking-widest uppercase mt-1.5">{correctCount} / 8 Correct</p>
                                </div>
                              </div>

                              <div className="space-y-1">
                                <h3 className="text-xl font-black text-slate-900 dark:text-white tracking-tight">
                                  {isPassed ? "Gold Standard Reinforcement!" : "Continuous Review Required"}
                                </h3>
                                <p className="text-xs font-bold text-slate-500 dark:text-slate-400 max-w-sm leading-relaxed">
                                  {isPassed 
                                    ? "Excellent clinical retention. You have locked down the key clinical concepts from this page."
                                    : "Take another look at the active lesson content to polish clinical accuracy and compliance protocols."
                                  }
                                </p>
                              </div>
                            </div>

                            {/* Question Breakdown List */}
                            <div className="space-y-3.5 max-h-[180px] overflow-y-auto pr-1 text-left">
                              {quizQuestions.map((q, idx) => {
                                const selectedOptId = selectedQuizAnswers[q._id];
                                const isCorrect = q.options.find((o: any) => o.isCorrect)?._id === selectedOptId;
                                return (
                                  <div key={q._id} className="flex items-start gap-4 p-4 bg-slate-50 dark:bg-slate-950/30 rounded-2xl border border-slate-150/50 dark:border-slate-850/50 animate-fade-in">
                                    <div className={`w-6 h-6 rounded-lg ${isCorrect ? 'bg-emerald-50 dark:bg-emerald-950/20 text-emerald-600 font-bold' : 'bg-rose-50 dark:bg-rose-950/20 text-rose-550 font-bold'} flex items-center justify-center shrink-0 shadow-sm font-black text-[10px]`}>
                                      {idx + 1}
                                    </div>
                                    <div className="space-y-1 flex-1 pr-2">
                                      <p className="text-[11px] font-bold text-slate-800 dark:text-slate-350 tracking-tight leading-relaxed">"{q.text}"</p>
                                    </div>
                                    {isCorrect ? (
                                      <CheckCircle className="text-emerald-550 shrink-0" size={16} />
                                    ) : (
                                      <XCircle className="text-rose-550 shrink-0" size={16} />
                                    )}
                                  </div>
                                );
                              })}
                            </div>

                            {/* ACTIONS */}
                            <div className="flex flex-col sm:flex-row gap-3 pt-4 justify-center">
                              <button 
                                onClick={handleTriggerQuickQuiz}
                                className="px-6 py-3.5 bg-white hover:bg-slate-50 dark:bg-slate-950 dark:hover:bg-slate-850 text-slate-700 dark:text-slate-350 rounded-xl text-xs font-black uppercase tracking-widest border border-slate-200 dark:border-slate-800 transition-all active:scale-95 cursor-pointer flex items-center justify-center gap-2 shadow-sm"
                              >
                                <Activity size={14} />
                                <span>Retake Quiz</span>
                              </button>
                              <button 
                                onClick={() => setIsQuizActive(false)}
                                className="px-8 py-3.5 bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 text-white rounded-xl text-xs font-black uppercase tracking-widest transition-all active:scale-95 cursor-pointer flex items-center justify-center gap-2 shadow-md"
                              >
                                <span>Continue Studying</span>
                                <ArrowRight size={14} />
                              </button>
                            </div>
                          </div>
                        );
                      })()
                    )}
                  </div>
                )}

              </div>
            </div>
          )}
        </div>
      )}

    </div>
  );
};

const renderModuleSlide = (module: StudyModule, slide: StudySlide, index: number, setExpandedImage: (url: string) => void, isPrintMode: boolean = false) => {
  const Icon = IconResolver(module.icon);
  
  const slideStyles = isPrintMode 
    ? "w-full bg-white relative" 
    : "w-full bg-white dark:bg-slate-900 rounded-2xl sm:rounded-[2.5rem] p-4 sm:p-8 md:p-12 shadow-xl border border-slate-100 dark:border-slate-800 border-l-4 sm:border-l-[8px] animate-slide-up relative overflow-hidden";

  const renderGlossaryItem = (term: string, definition: string, i: number) => (
    <div key={i} className={`group p-6 rounded-[2rem] transition-all duration-300 ${isPrintMode ? 'bg-white border-2 border-slate-100' : 'bg-slate-50 border border-slate-100 hover:border-emerald-500 hover:bg-white hover:shadow-xl hover:translate-y-[-2px]'}`}>
      <div className="flex items-center gap-3 mb-3">
        <div className="w-8 h-8 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0 font-black text-[10px] group-hover:bg-emerald-500 group-hover:text-white transition-all">
          {i + 1}
        </div>
        <h4 className="text-base font-black text-slate-900 tracking-tight">{term}</h4>
      </div>
      <p className="text-[10px] md:text-xs font-black text-slate-700 leading-relaxed tracking-tight opacity-90 group-hover:opacity-100 transition-opacity">
        {definition}
      </p>
    </div>
  );

  const content = (() => {
    if (!slide) return null;
    switch (slide.type) {
      case 'infographic':
        return (
          <div className={`${slideStyles} ${!isPrintMode && 'border-indigo-500'} ${isPrintMode ? 'p-0 border-none' : '!p-0'} min-h-0 flex flex-col`}>
             <div className="flex flex-col flex-1">
                <div className={isPrintMode ? "w-full rounded-[2.5rem] overflow-hidden bg-slate-900 border border-slate-200" : "relative group bg-slate-100 overflow-hidden min-h-[300px] md:min-h-[400px]"}>
                    {slide.data?.imageUrl ? (
                        <>
                            <img src={slide.data.imageUrl} className={`w-full h-full ${isPrintMode ? 'print-image-fit' : 'object-contain'} transition-transform duration-700 group-hover:scale-105`} alt={slide.data.visualAlt || "Clinical Diagram"} />
                            {!isPrintMode && (
                                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                    <button onClick={() => setExpandedImage(slide.data.imageUrl!)} className="p-4 bg-white text-slate-900 rounded-full shadow-2xl hover:scale-110 transition-transform flex items-center gap-2">
                                        <Maximize2 size={20} />
                                        <span className="text-[10px] font-black tracking-widest">Full Screen</span>
                                    </button>
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center text-slate-600 gap-4 py-20">
                            <ImageIcon size={64} className="opacity-10" />
                            <p className="text-[10px] font-black tracking-[0.3em] opacity-30">Syncing Asset...</p>
                        </div>
                    )}
                </div>
                <div className={isPrintMode ? "p-8 space-y-8" : "p-4 sm:p-8 md:p-12 space-y-6 sm:space-y-10 bg-white dark:bg-slate-900"}>
                    <div className="print-no-cut space-y-4 max-w-4xl">
                        <div className="flex items-center gap-3">
                            <div className="p-2.5 bg-indigo-50 text-indigo-600 rounded-2xl border border-indigo-100 shadow-sm">
                                <Brain size={24} />
                            </div>
                            <div>
                                <h2 className="text-xl md:text-2xl font-black text-slate-900 tracking-tight leading-none">{slide.title || "Visual Logics"}</h2>
                                <p className="text-[10px] font-bold text-slate-400 tracking-widest mt-1.5">Clinical Pattern Recognition</p>
                            </div>
                        </div>
                        {!isPrintMode && <p className="text-slate-500 font-medium text-sm md:text-base leading-relaxed pl-1 max-w-2xl">High-yield clinical recognition patterns optimized for registry accuracy.</p>}
                    </div>

                    <div className="space-y-6 print-no-cut max-w-4xl">
                        <h4 className="text-[10px] font-black text-slate-400 tracking-[0.2em] flex items-center gap-2">
                             <ShieldCheck size={16} className="text-emerald-500" /> Mastery Observations
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {(slide.data?.observations ?? []).map((obs: string, i: number) => (
                                <div key={i} className={`flex items-start gap-4 p-5 bg-slate-50 rounded-2xl border border-slate-100 ${!isPrintMode && 'hover:border-indigo-300 hover:bg-white hover:shadow-lg transition-all group'}`}>
                                    <div className="w-6 h-6 rounded-lg bg-white border border-slate-200 flex items-center justify-center shrink-0 shadow-sm font-black text-[10px] text-slate-400 group-hover:bg-indigo-600 group-hover:text-white transition-all">{i+1}</div>
                                    <p className="text-[10px] md:text-xs font-black text-slate-800 tracking-tight leading-tight opacity-90 group-hover:opacity-100 leading-relaxed">"{obs}"</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
             </div>
          </div>
        );
      case 'overview':
        return (
          <div className={`${slideStyles} ${!isPrintMode && 'border-indigo-500'} ${isPrintMode ? 'p-0 border-none' : 'p-4 sm:p-8 md:p-12'}`}>
             <div className="relative z-10 max-w-5xl mx-auto space-y-12">
                {!isPrintMode && (
                    <div className="w-16 h-16 bg-indigo-50 text-indigo-600 rounded-[2rem] flex items-center justify-center mx-auto mb-6 shadow-xl border border-indigo-100 animate-bounce-slow">
                        <Icon size={32} />
                    </div>
                )}
                <div className="text-center print-no-cut space-y-3">
                    <div className="inline-flex items-center gap-2 px-4 py-1 bg-slate-100 text-slate-500 rounded-full text-[9px] font-black tracking-widest border border-slate-200">
                        Lesson Overview
                    </div>
                    <h1 className="text-2xl md:text-4xl font-black text-slate-900 leading-tight tracking-tight">{module.title}</h1>
                    <div className="h-1 w-20 bg-indigo-500 mx-auto rounded-full"></div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left">
                    <div className="group p-8 bg-indigo-50/40 rounded-[2.5rem] border border-indigo-100/50 hover:bg-white hover:shadow-2xl transition-all duration-500 print-no-cut relative overflow-hidden">
                       <h4 className="text-[9px] font-black text-indigo-600 tracking-widest flex items-center gap-2 mb-4">Theoretical Context</h4>
                       <p className="text-[10px] md:text-xs font-black text-slate-800 leading-tight tracking-tight opacity-90">"{slide.data?.importance}"</p>
                    </div>
                    <div className="group p-8 bg-emerald-50/40 rounded-[2.5rem] border border-emerald-100/50 hover:bg-white hover:shadow-2xl transition-all duration-500 print-no-cut relative overflow-hidden">
                       <h4 className="text-[9px] font-black text-emerald-600 tracking-widest flex items-center gap-2 mb-4">Registry Logic</h4>
                       <p className="text-[10px] md:text-xs font-black text-slate-800 leading-tight tracking-tight opacity-90">"{slide.data?.examContext}"</p>
                    </div>
                </div>

                {slide.data?.observations && slide.data.observations.length > 0 && (
                    <div className="pt-12 border-t border-slate-100 space-y-8">
                        <div className="flex flex-col items-center gap-3 print-no-cut">
                            <h3 className="text-xl font-black text-slate-900 tracking-tight">Clinical Focus Points</h3>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {slide.data.observations.map((obs: string, i: number) => (
                                <div key={i} className={`flex gap-4 p-6 bg-slate-50 rounded-[2rem] border border-slate-100 text-left print-no-cut hover:border-indigo-300 hover:bg-white hover:shadow-lg transition-all group`}>
                                    <div className="w-7 h-7 rounded-lg bg-white border border-slate-200 flex items-center justify-center shrink-0 font-black text-[10px] text-slate-400 group-hover:bg-indigo-600 group-hover:text-white transition-all shadow-sm">{i+1}</div>
                                    <p className="text-[10px] md:text-xs font-black text-slate-800 tracking-tight opacity-90 group-hover:opacity-100 leading-tight">"{obs}"</p>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
             </div>
          </div>
        );
      case 'definitions':
        return (
          <div className={`${slideStyles} ${!isPrintMode && 'border-emerald-500'} ${isPrintMode ? 'p-0 border-none' : 'p-4 sm:p-8 md:p-12'}`}>
             <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10 border-b border-slate-100 pb-6">
                <div className="space-y-1">
                    <h2 className="text-xl md:text-2xl font-black text-slate-900 tracking-tight flex items-center gap-3">
                        <BookOpen className="text-emerald-500 shrink-0" size={28} /> Glossary Matrix
                    </h2>
                    <p className="text-slate-400 font-bold text-[9px] tracking-widest pl-10"> Registry Logic Reinforcement </p>
                </div>
             </div>
             
             <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                {(slide.data?.items ?? []).map((def: any, i: number) => renderGlossaryItem(def.term, def.definition, i))}
             </div>
          </div>
        );
      case 'responsibilities': {
          const doCount = (slide.data?.do || []).length;
          const reportCount = (slide.data?.report || []).length;
          const dontCount = (slide.data?.dont || []).length;
          
          return (
            <div className={`${slideStyles} ${!isPrintMode && 'border-rose-500'} ${isPrintMode ? 'p-0 border-none' : 'p-4 sm:p-8 md:p-10'}`}>
               <div className="mb-10">
                   <h2 className="text-xl md:text-2xl font-black text-slate-900 tracking-tight flex items-center gap-3">
                        <Ban className="text-rose-500 shrink-0" size={28} /> Scope Matrix
                   </h2>
                   <p className="text-[9px] font-black text-slate-400 tracking-widest mt-2">Authorized Theoretical Domain</p>
               </div>

               <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* DO */}
                  <div className="group flex flex-col h-full">
                      <div className="flex items-center justify-between mb-3 px-2">
                         <h4 className="text-[10px] font-black text-emerald-600 tracking-widest flex items-center gap-2">
                             <CheckCircle2 size={14} /> Authorized
                         </h4>
                      </div>
                      <div className="flex-1 p-6 bg-emerald-50 rounded-[2rem] border border-emerald-100 group-hover:bg-white group-hover:shadow-xl transition-all duration-500 space-y-4">
                          {(slide.data?.do || []).map((item: string, i: number) => (
                              <div key={i} className="flex gap-3 p-3 bg-white/50 rounded-xl border border-emerald-50/50 text-xs font-bold text-emerald-950 leading-relaxed tracking-tight shadow-sm">
                                  <div className="h-1.5 w-1.5 rounded-full bg-emerald-500 mt-1.5 shrink-0"></div>
                                  <span>{item}</span>
                              </div>
                          ))}
                      </div>
                  </div>

                  {/* REPORT */}
                  <div className="group flex flex-col h-full">
                      <div className="flex items-center justify-between mb-3 px-2">
                         <h4 className="text-[10px] font-black text-indigo-600 tracking-widest flex items-center gap-2">
                             <MessageSquare size={14} /> Reporting
                         </h4>
                      </div>
                      <div className="flex-1 p-6 bg-indigo-50 rounded-[2rem] border border-indigo-100 group-hover:bg-white group-hover:shadow-xl transition-all duration-500 space-y-4">
                          {(slide.data?.report || []).map((item: string, i: number) => (
                              <div key={i} className="flex gap-3 p-3 bg-white/50 rounded-xl border border-indigo-50/50 text-xs font-bold text-indigo-950 leading-relaxed tracking-tight shadow-sm">
                                  <div className="h-1.5 w-1.5 rounded-full bg-indigo-500 mt-1.5 shrink-0 animate-pulse"></div>
                                  <span>{item}</span>
                              </div>
                          ))}
                      </div>
                  </div>

                  {/* DONT */}
                  <div className="group flex flex-col h-full">
                      <div className="flex items-center justify-between mb-3 px-2">
                         <h4 className="text-[10px] font-black text-rose-600 tracking-widest flex items-center gap-2">
                             <AlertTriangle size={14} /> Restricted
                         </h4>
                      </div>
                      <div className="flex-1 p-6 bg-rose-50 rounded-[2rem] border border-rose-100 group-hover:bg-white group-hover:shadow-xl transition-all duration-500 space-y-4">
                          {(slide.data?.dont || []).map((item: string, i: number) => (
                              <div key={i} className="flex gap-3 p-3 bg-white/50 rounded-xl border border-rose-50/50 text-xs font-bold text-rose-950 leading-relaxed tracking-tight shadow-sm">
                                  <div className="h-1.5 w-1.5 rounded-full bg-rose-500 mt-1.5 shrink-0"></div>
                                  <span className="opacity-80 transition-opacity">{item}</span>
                              </div>
                          ))}
                      </div>
                  </div>
               </div>
            </div>
          );
      }
      case 'process':
        return (
          <div className={`${slideStyles} ${!isPrintMode && 'border-amber-500'} ${isPrintMode ? 'p-0 border-none' : 'p-4 sm:p-8 md:p-10'}`}>
             <div className="mb-8 space-y-2">
                <h2 className="text-xl md:text-2xl font-black text-slate-900 tracking-tight flex items-center gap-3">
                    <Layers className="text-amber-500 shrink-0" size={28} /> Protocol Pipeline
                </h2>
                <p className="text-[9px] font-black text-slate-400 tracking-widest pl-10 opacity-60">Sequential Theoretical Execution</p>
             </div>

             <div className="relative space-y-4 pl-6 sm:pl-8 md:pl-10 border-l-2 border-slate-100 ml-4 sm:ml-8 md:ml-10 py-2">
                {(slide.data?.steps ?? []).map((step: string, i: number) => (
                    <div key={i} className="relative flex gap-4 sm:gap-6 p-4 sm:p-6 bg-slate-50 rounded-[2rem] border border-slate-100 items-start print-no-cut hover:bg-white hover:shadow-xl hover:translate-x-1 transition-all group">
                        <div className="absolute -left-[35px] sm:-left-[41px] md:-left-[63px] top-1/2 -translate-y-1/2 w-7 h-7 sm:w-8 sm:h-8 md:w-12 md:h-12 rounded-full bg-white border-2 border-slate-100 flex items-center justify-center shrink-0 font-black text-xs md:text-base text-slate-300 group-hover:bg-amber-500 group-hover:text-white group-hover:border-amber-500 transition-all shadow-lg z-10">
                            {i+1}
                        </div>
                        <div className="space-y-1 flex-1">
                            <p className="text-sm md:text-lg font-bold text-slate-800 leading-relaxed tracking-tight opacity-90 transition-opacity">
                                "{step}"
                            </p>
                        </div>
                    </div>
                ))}
             </div>
          </div>
        );
      case 'examFocus':
        return (
          <div className={`${slideStyles} ${!isPrintMode && 'border-orange-500'} ${isPrintMode ? 'p-0 border-none' : 'p-4 sm:p-8 md:p-10'}`}>
             <h2 className="text-xl md:text-2xl font-black text-slate-900 tracking-tight mb-8 flex items-center gap-3 print-no-cut"><ShieldAlert className="text-orange-500 shrink-0" size={28} /> Board Logic Traps</h2>
             <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-6">
                   <h4 className="text-[10px] font-black text-emerald-600 tracking-widest px-2 flex items-center gap-2 print-no-cut border-b border-emerald-100 pb-2"><Lightbulb size={16}/> High-Yield Facts</h4>
                   <div className="space-y-3">
                      {(slide.data?.facts ?? []).map((fact: string, i: number) => (
                        <div key={i} className="p-6 bg-emerald-50/40 rounded-[2rem] border border-emerald-100 text-sm md:text-base font-bold text-slate-800 leading-relaxed print-no-cut hover:bg-white transition-all shadow-sm">"{fact}"</div>
                      ))}
                   </div>
                </div>
                <div className="space-y-6">
                   <h4 className="text-[10px] font-black text-rose-600 tracking-widest px-2 flex items-center gap-2 print-no-cut border-b border-rose-100 pb-2"><AlertTriangle size={16}/> Common Concepts</h4>
                   <div className="space-y-3">
                      {(slide.data?.mistakes ?? []).map((mistake: string, i: number) => (
                        <div key={i} className="p-6 bg-rose-50/40 rounded-[2rem] border border-rose-100 text-sm md:text-base font-bold text-slate-800 leading-relaxed print-no-cut hover:bg-white transition-all shadow-sm">"{mistake}"</div>
                      ))}
                   </div>
                </div>
             </div>
          </div>
        );
      case 'scenario':
        return (
          <div className={`${slideStyles} ${!isPrintMode && 'border-indigo-600'} ${isPrintMode ? 'p-0 border-none' : 'p-4 sm:p-8 md:p-10'}`}>
             <h2 className="text-xl md:text-2xl font-black text-slate-900 tracking-tight mb-10 flex items-center gap-3 print-no-cut"><Stethoscope className="text-indigo-600 shrink-0" size={28} /> Clinical Simulation</h2>
             <div className="space-y-8 max-w-4xl">
                <div className="p-8 md:p-10 bg-slate-900 rounded-[2rem] text-white shadow-2xl relative overflow-hidden print-no-cut group">
                    <h4 className="text-[9px] font-black text-indigo-400 tracking-widest mb-4">Theoretical Setting</h4>
                    <p className="text-lg md:text-xl font-bold leading-relaxed text-white/90 group-hover:text-white transition-colors">"{slide.data?.situation}"</p>
                </div>
                <div className="p-8 md:p-10 bg-emerald-50 rounded-[2rem] border-2 border-emerald-100 shadow-xl print-no-cut group relative overflow-hidden">
                    <h4 className="text-[9px] font-black text-emerald-600 tracking-widest mb-4">Master Action Protocol</h4>
                    <p className="text-base md:text-lg font-black text-emerald-950 leading-snug relative z-10">{slide.data?.action}</p>
                </div>
             </div>
          </div>
        );
      case 'review':
        return (
          <div className={`${slideStyles} ${!isPrintMode && 'border-purple-600'} ${isPrintMode ? 'p-0 border-none' : 'p-4 sm:p-8 md:p-10'}`}>
             <h2 className="text-xl md:text-2xl font-black text-slate-900 tracking-tight mb-10 flex items-center gap-3 print-no-cut"><Star className="text-purple-600 shrink-0" size={28} /> Lesson Synthesis</h2>
             <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10">
                <div className="space-y-6">
                   <h4 className="text-[10px] font-black text-slate-400 tracking-widest px-2 print-no-cut border-b border-slate-100 pb-2">Unit Logic Anchors</h4>
                   <div className="space-y-3">
                      {(slide.data?.summary ?? []).map((item: string, i: number) => (
                        <div key={i} className="flex gap-4 p-5 bg-slate-50 rounded-[2rem] border border-slate-100 print-no-cut hover:bg-white hover:shadow-xl transition-all group">
                            <div className="p-2 bg-white rounded-xl shadow-sm text-indigo-500 border border-slate-100 group-hover:bg-indigo-600 group-hover:text-white transition-all"><CheckCircle2 size={16} /></div>
                            <p className="text-xs md:text-sm font-bold text-slate-700 leading-relaxed tracking-tight opacity-80 group-hover:opacity-100 leading-relaxed">{item}</p>
                        </div>
                      ))}
                   </div>
                </div>
                <div className="space-y-6">
                   <h4 className="text-[10px] font-black text-slate-400 tracking-widest px-2 print-no-cut border-b border-slate-100 pb-2">Cognitive Mnemonics</h4>
                   <div className="space-y-3">
                      {(slide.data?.mnemonics ?? []).map((item: string, i: number) => (
                        <div key={i} className="p-6 bg-indigo-50/50 rounded-[2rem] border border-indigo-100 flex items-center gap-5 print-no-cut hover:bg-white hover:shadow-xl transition-all group">
                            <div className="p-2 bg-white rounded-xl shadow-sm text-indigo-600 group-hover:bg-indigo-600 group-hover:text-white transition-all border border-indigo-50"><Brain size={24} /></div>
                            <p className="text-sm md:text-base font-black text-indigo-950 tracking-tight leading-tight">"{item}"</p>
                        </div>
                      ))}
                   </div>
                </div>
             </div>
          </div>
        );
      case 'embedded':
        return (
          <div className={`${slideStyles} ${!isPrintMode && 'border-indigo-600'} ${isPrintMode ? 'p-0 border-none' : 'p-4 sm:p-8 md:p-12'} flex flex-col h-full`}>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-100 pb-6 mb-8">
              <div className="space-y-1">
                <h2 className="text-xl md:text-2xl font-black text-slate-900 tracking-tight mt-1.5">{slide.title || "Study Module"}</h2>
              </div>
              {!isPrintMode && (
                <div className="flex items-center gap-2">
                  <span className="px-3 py-1 bg-indigo-50 border border-indigo-150 text-indigo-700 text-[9px] font-black tracking-widest uppercase rounded-full">
                    Interactive
                  </span>
                </div>
              )}
            </div>

            <EmbeddedPhasePlayer 
              embedMode={slide.data?.embedMode || 'url'}
              embedUrl={slide.data?.embedUrl}
              embedCode={slide.data?.embedCode}
              aspectRatio={slide.data?.aspectRatio || '16:9'}
              embedWidth={slide.data?.embedWidth}
              embedHeight={slide.data?.embedHeight}
              title={slide.title}
              videoTitle={slide.data?.videoTitle}
              videoAuthor={slide.data?.videoAuthor}
              videoAuthorUrl={slide.data?.videoAuthorUrl}
              videoThumbnail={slide.data?.videoThumbnail}
              videoNotes={slide.data?.videoNotes}
              videoDescription={slide.data?.videoDescription}
            />
          </div>
        );
      default:
        return (
          <div className={`${slideStyles} border-slate-500 p-8`}>
            <div className="flex flex-col items-center justify-center gap-4 py-12 text-slate-400">
               <Info size={48} className="opacity-20" />
               <p className="text-[10px] font-black tracking-widest">Protocol Type Not Identified</p>
               <h4 className="text-xl font-black text-slate-900">{slide.type}</h4>
            </div>
          </div>
        );
    }
  })();

  return (
    <article className={`w-full flex flex-col items-center ${!isPrintMode && 'px-1 md:px-0'}`}>
      <div className="w-full max-w-5xl">{content}</div>
    </article>
  );
};